// packages/charts/src/formatTokens.ts
//
// The one colour map behind conditional formatting (ADR-0351, decision 4).
// A rule names a TOKEN, never a CSS value, so the stored spec carries no
// injection surface and every theme (app light/dark, the mailed report) can
// resolve the same rule to colours that stay readable.
//
// Pure: no React, no DOM. Surfaces pass their theme and palette in.

/** Semantic tokens. "positive"/"negative" are what a rule says when it means
 *  good/bad; they are the same greens and reds as the heatmap tint. */
export const SEMANTIC_TOKENS = ["positive", "negative", "warning", "neutral", "accent"] as const;
export type SemanticToken = (typeof SEMANTIC_TOKENS)[number];

/** Palette slots: the account chart palette, 1-based like the UI shows them. */
export const PALETTE_TOKENS = [
  "palette_1", "palette_2", "palette_3", "palette_4",
  "palette_5", "palette_6", "palette_7", "palette_8",
] as const;
export type PaletteToken = (typeof PALETTE_TOKENS)[number];

export type ColorToken = SemanticToken | PaletteToken;

export const COLOR_TOKENS: readonly ColorToken[] = [...SEMANTIC_TOKENS, ...PALETTE_TOKENS];

// Own-key lookup only: a stored token of "constructor" or "__proto__" must
// resolve to nothing, never to an Object.prototype member.
const TOKEN_SET: ReadonlySet<string> = new Set(COLOR_TOKENS);

export function isColorToken(value: unknown): value is ColorToken {
  return typeof value === "string" && TOKEN_SET.has(value);
}

export type FormatTheme = "light" | "dark";

type Rgb = readonly [number, number, number];

// Positive/negative are the heatmap's colours (heatTint.ts), so a rule's
// "negative" and the heat tint's red are one red.
const SEMANTIC_RGB: ReadonlyMap<SemanticToken, Rgb> = new Map<SemanticToken, Rgb>([
  ["positive", [43, 138, 62]],
  ["negative", [201, 42, 42]],
  ["warning", [230, 119, 0]],
  ["neutral", [134, 142, 150]],
  ["accent", [34, 139, 230]],
]);

/** The page backgrounds and default text a fill is composited over and read
 *  against. Light = the white page/mail; dark = Mantine dark-7 + dark-0. */
export const THEME_SURFACE: Readonly<Record<FormatTheme, { background: Rgb; text: Rgb }>> = {
  light: { background: [255, 255, 255], text: [33, 37, 41] },
  dark: { background: [26, 27, 30], text: [193, 194, 197] },
};

/** Fill opacity of a token fill. Low enough that the theme's default text
 *  stays AA on it (asserted in formatTokens.test.ts), high enough to read. */
export const FILL_ALPHA: Readonly<Record<FormatTheme, number>> = { light: 0.22, dark: 0.32 };

// The account palette arrives as hex strings (validated upstream by
// HEX_COLOR.fullmatch); anything else is ignored rather than injected.
const HEX6 = /^#[0-9a-fA-F]{6}$/;

function hexToRgb(hex: string): Rgb | null {
  if (!HEX6.test(hex)) return null;
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

export function tokenRgb(token: ColorToken, palette?: readonly string[]): Rgb | null {
  const semantic = SEMANTIC_RGB.get(token as SemanticToken);
  if (semantic) return semantic;
  const idx = PALETTE_TOKENS.indexOf(token as PaletteToken);
  if (idx < 0) return null;
  const hex = palette?.[idx];
  return typeof hex === "string" ? hexToRgb(hex) : null;
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `fg` at `alpha` over `bg`, as an opaque colour. */
export function composite(fg: Rgb, alpha: number, bg: Rgb): Rgb {
  const mix = (f: number, b: number) => Math.round(f * alpha + b * (1 - alpha));
  return [mix(fg[0], bg[0]), mix(fg[1], bg[1]), mix(fg[2], bg[2])];
}

function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const m = (x: number, y: number) => Math.round(x + (y - x) * t);
  return [m(a[0], b[0]), m(a[1], b[1]), m(a[2], b[2])];
}

/** Move `rgb` toward black (light theme) or white (dark theme) until it
 *  reaches WCAG AA (4.5) against `against`. Twenty 5 % steps always get
 *  there, because pure black/white clears 4.5 against either surface. */
export function ensureContrast(rgb: Rgb, against: Rgb, theme: FormatTheme, min = 4.5): Rgb {
  const target: Rgb = theme === "light" ? [0, 0, 0] : [255, 255, 255];
  let out = rgb;
  for (let i = 1; i <= 20 && contrastRatio(out, against) < min; i += 1) {
    out = mixRgb(rgb, target, i / 20);
  }
  return out;
}

const css = ([r, g, b]: Rgb, alpha?: number) =>
  alpha === undefined ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;

/** What the evaluator decides a cell's background is, before a theme turns
 *  it into a colour. */
export type CfPaint =
  /** A token fill. */
  | { kind: "token"; token: ColorToken }
  /** The heatmap ramp: -1 (worst) .. 1 (best), direction already applied. */
  | { kind: "sentiment"; strength: number }
  /** A colour-scale position between two tokens. */
  | { kind: "blend"; from: ColorToken; to: ColorToken; t: number };

export type ResolveOptions = {
  theme: FormatTheme;
  /** The account chart palette (hex), for palette_N tokens. */
  palette?: readonly string[];
  /** Mailed reports: an opaque colour composited over the page, because
   *  not every mail client honours rgba. */
  opaque?: boolean;
};

/** A paint as a CSS background, or undefined when it cannot be resolved (an
 *  unknown token, a palette slot the account has no colour for). */
export function resolveFill(paint: CfPaint, opts: ResolveOptions): string | undefined {
  const surface = THEME_SURFACE[opts.theme].background;
  if (paint.kind === "sentiment") {
    // Exactly the heat tint that existed before ADR-0351 (heatTint.ts; the
    // inline table golden pins these strings).
    const alpha = 0.08 + 0.3 * Math.min(1, Math.abs(paint.strength));
    const rgb = paint.strength >= 0 ? SEMANTIC_RGB.get("positive")! : SEMANTIC_RGB.get("negative")!;
    return opts.opaque ? css(composite(rgb, alpha, surface)) : css(rgb, alpha);
  }
  let rgb: Rgb | null;
  if (paint.kind === "token") {
    rgb = tokenRgb(paint.token, opts.palette);
  } else {
    const a = tokenRgb(paint.from, opts.palette);
    const b = tokenRgb(paint.to, opts.palette);
    rgb = a && b ? mixRgb(a, b, Math.max(0, Math.min(1, paint.t))) : null;
  }
  if (!rgb) return undefined;
  const alpha = FILL_ALPHA[opts.theme];
  return opts.opaque ? css(composite(rgb, alpha, surface)) : css(rgb, alpha);
}

/** A token as a text colour that reaches AA against the theme's surface. */
export function resolveTextColor(token: ColorToken, opts: ResolveOptions): string | undefined {
  const rgb = tokenRgb(token, opts.palette);
  if (!rgb) return undefined;
  const surface = THEME_SURFACE[opts.theme].background;
  return css(ensureContrast(rgb, surface, opts.theme));
}
